/**
 * costBreakdownModel.js — 정산 탭 '원가 · 마진' 표(CostBreakdownTable.vue)의 줄 만들기 (순수 함수).
 *
 * 화면과 엑셀 내보내기(원가·마진 영역, 계획서 ~/md/plan/콜봇-월정산-정산시작일-엑셀내보내기-계획.md 4-5절)가 같은 줄을 쓰게
 * 컴포넌트에서 떼어 냈다. 화면 문구·순서·규칙은 컴포넌트 주석(화면 명세 2-3)을 따른다 — 여기서 바꾸면 화면이 바뀐다.
 *
 * 셀 규칙
 *  - 값 셀은 `{ text, value }` + 꾸밈 표시(note·ref·missing·muted·negative·source). text 는 화면 문구 그대로, value 는 원 단위
 *    숫자(금액 칸 — 원가·서티 금액·마진·합계)이고 숫자가 아니면 null. 사용량·단가 칸은 문구만이라 value 가 늘 null 이다
 *    (계획서 4-5 "금액 칸은 숫자, 단가 칸은 화면 문구").
 *  - ref:true 인 원가(PBX 트렁크·STT 추정)는 합계에 넣지 않는 참고값이다 — value 가 있어도 더하지 않는다.
 *
 * 줄 규칙
 *  - kind: 'section'(서티 청구 / 원가 머리) · 'item'(값 칸 일곱) · 'detail'(근거 문장 하나가 값 칸 전체를 덮음).
 *  - level: item·detail 은 0(항목) / 1(자식). section 은 null.
 *  - area: 'sale'(서티 청구) | 'cost'(원가) — 엑셀 '구역' 열.
 *
 * 옵션
 *  - withNames(기본 true): false 면 상담사 번호 '가장 많았던 날' 근거에 번호 이름·끝 4자리(`peak_numbers`)를 쓰지 않는다
 *    ('11/4 5개'까지만). 회선 끝 4자리는 병원 회선 표시라 그대로 둔다.
 *  - withCost(기본 true): 이 표는 전체가 원가·마진 표라 원가를 뺀 모양이 없다. 엑셀은 원가·마진을 골랐을 때만 이 표를
 *    쓴다(4-5 "원가·마진 영역(원가·마진을 골랐을 때)") — withCost:false 는 호출 실수라 던진다.
 *  - 두 옵션 모두 빠지면(undefined) true, true·false 가 아닌 값(null·0·'' 등)은 TypeError(sheetOptions.flagOption — 정산서 표
 *    모델과 같은 판정).
 *
 * 정산 시작일(계획서 4-3, 2·3단계 계약서 3-4) — 옵션을 늘리지 않고 상세 칸을 보고 스스로 판단한다. 시작일 칸이 없는 달의 출력은
 * 그대로다.
 *  - 시작 월(cost.billable·cost.pre_start 둘 다 있음 — settlementRules.billableCostOf): 원가 항목 줄은 달 전체 원가 그대로(사용량
 *    칸과 같은 모집단), 원가 구역 끝에 '시작 전 원가(우리 부담)' 참고 줄(합계에서 뺌). 합계 원가·마진은 청구분(cost.billable)
 *    기준이라 서버 마진(청구 − cost.billable)과 같다. 웹 텍스트 행 마진은 청구분 웹 원가로, 회선별 마진은 회선마다의 청구분
 *    원가가 없어 '합계에서'로 읽는다. 참고 마진에 '시작 전 원가까지 넣으면'(달 전체 원가 기준)을 더한다.
 *  - 정산 대상 아님(billing_start.scope 'before_start'): 서티 단가·금액·마진 칸은 빈칸, 요율 없음 안내를 띄우지 않고, 합계는
 *    원가만(이 달 원가는 모두 우리 부담).
 *
 * vue·antd·xlsx 를 import 하지 않는다 — node --test 로 검증한다(service/__tests__/costBreakdownModel.test.mjs).
 */

import {
  EMPTY,
  daysInMonth,
  formatDuration,
  formatNumber,
  formatUnitPrice,
  formatWon,
  isNumber,
  maskNumber,
} from './settlementFormat.js';
import {
  COUNSELOR_LINE_COST_TEXT,
  COUNSELOR_LINE_NO_RATE_TEXT,
  aiTimeCalls,
  baseIncludesText,
  billableCostOf,
  chargeSourceLabel,
  connectModeLabel,
  counselorItemLabel,
  counselorNumberLineView,
  detailConnectMode,
  isInternalDetail,
  isLegacyStatementDetail,
  isNotBillableDetail,
  legacyPlanVersionOf,
  legacyStatementActionText,
  lineOverride,
  lineTypeLabel,
  oneOffAmountOf,
  outboundCountText,
  overageFormulaText,
  pairLines,
  statementStatusOf,
  summaryLine,
  unitsMinutesText,
  vatOf,
  warningMeta,
  webUsageNotice,
} from './settlementRules.js';
import { preStartUsageShortText, settlementScopeView, shortDateText, startMonthWindowText } from './settlementStart.js';
import { flagOption } from './sheetOptions.js';

/** 정산 대상 아님 달의 합계 줄 메모 */
export const NOT_BILLABLE_COST_NOTE = '정산 대상 아님 — 이 달 원가는 모두 우리 부담입니다';
/** 시작 전 원가 줄 이름 */
export const PRE_START_COST_LABEL = '시작 전 원가(우리 부담)';

/** 기본 요율 없이 사람이 넣은 금액만 있는 달의 안내 */
export const MANUAL_ONLY_TEXT =
  '그 달에 적용할 기본 요율 버전이 없어 계산값은 없습니다. 사람이 넣은 항목 금액과 일회성 항목만 합계에 들어가고, 마진은 계산하지 않습니다.';

/** Amount source badge — `charge.source` */
const SOURCE_TEXT = {
  confirmed: '확정 금액 · 마감 시점 스냅샷',
  review: '검토 금액 · 마감 시점 스냅샷',
  estimate: '추정 금액 · 지금 사용량·요율로 계산',
};

// ---------------------------------------------------------------------------
// Number helpers — intermediate amounts (15,531.78) keep two decimals
// ---------------------------------------------------------------------------

const round2 = (x) => Math.round((x + Number.EPSILON) * 100) / 100;
const numOr = (v) => (isNumber(v) ? v : null);
const minus = (a, b) => (isNumber(a) && isNumber(b) ? round2(a - b) : null);
const isNegative = (v) => isNumber(v) && v < 0;
const arr = (v) => (Array.isArray(v) ? v : []);
const obj = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : null);
// Usage is counted in 10-second units (units_10s) but shown as time — 'n단위' means nothing to the reader
const timeOf = (n) => (isNumber(n) ? unitsMinutesText(n) : EMPTY);

// ---------------------------------------------------------------------------
// Cells
// ---------------------------------------------------------------------------

/** 빈 칸(아무것도 그리지 않음) */
const BLANK = Object.freeze({ text: '', value: null });
/** '—' 칸 */
const NONE = Object.freeze({ text: EMPTY, value: null });

/** 문구만 있는 칸(사용량·단가·근거) */
const textCell = (text, extra = {}) => ({ text, value: null, ...extra });

/** 금액 칸 — text 는 formatWon, value 는 그 숫자 */
const wonCell = (value, extra = {}) => ({ text: formatWon(value), value: numOr(value), ...extra });

/**
 * 상세 응답을 원가 · 마진 표 모델로.
 * @param {object} detail 정산 상세 응답(요청 계약 2)
 * @param {{
 *   month?: string,
 *   summary?: ReturnType<typeof summaryLine>,
 *   live?: { supply: number|null, margin: number|null }|null,
 *   withNames?: boolean,
 *   withCost?: boolean,
 * }} [opts]
 *   month: 'YYYY-MM'(회선 일할의 그 달 일수, 요율 화면 링크). 없으면 detail.month.
 *   summary: settlementRules.summaryLine(detail). 없으면 여기서 만든다.
 *   live: 검토 입력 중인 { supply, margin } — 합계·마진만 따른다(항목별 서티 금액은 저장된 금액).
 * @returns {CostBreakdownModel}
 *
 * @typedef {{ text: string, value: number|null, note?: string, ref?: boolean, missing?: boolean, muted?: boolean,
 *   negative?: boolean, source?: 'default'|'override'|null }} Cell
 * @typedef {{ key: string, kind: 'section', area: 'sale'|'cost', level: null, name: string, sub: string }} SectionRow
 * @typedef {{ key: string, kind: 'detail', area: 'sale'|'cost', level: 1, name: string, sub: string, detail: Cell }} DetailRow
 * @typedef {{ key: string, kind: 'item', area: 'sale'|'cost', level: 0|1, name: string, sub: string, usage: Cell,
 *   costUnit: Cell, cost: Cell, saleUnit: Cell, sale: Cell, margin: Cell, warning?: string|null, partial?: boolean,
 *   partialTip?: string, override?: object|null, webNotice?: { label: string, tooltip: string }|null }} ItemRow
 * @typedef {{
 *   flags: { legacy: boolean, priced: boolean, manualOnly: boolean, internal: boolean, drift: boolean, live: boolean,
 *     startMonth: boolean, notBillable: boolean },
 *   source: { label: string, tone: 'muted'|'accent' },
 *   legacyNext: string, legacyText: string, planMissingText: string, planMissingLinkText: string,
 *   rows: Array<SectionRow|DetailRow|ItemRow>,
 *   totals: { cost: Cell, supply: Cell, vat: Cell, totalWithVat: Cell, margin: Cell, notes: string[], partialTip: string },
 *   referenceMarginText: string,
 * }} CostBreakdownModel
 */
export function costBreakdownModel(detail, opts = {}) {
  const { live = null } = opts;
  // 빠지면 true, true·false 가 아니면 던진다(sheetOptions.flagOption — 정산서 모델과 같은 판정)
  const withNames = flagOption(opts, 'withNames');
  if (!flagOption(opts, 'withCost')) {
    throw new TypeError(
      'costBreakdownModel: withCost:false 는 없다 — 원가 · 마진 표는 원가·마진을 골랐을 때만 쓴다(계획서 4-5)'
    );
  }
  const d = detail ?? {};
  const month = opts.month ?? d.month;
  const summary = opts.summary ?? summaryLine(d);

  // -------------------------------------------------------------------------
  // Response pieces
  // -------------------------------------------------------------------------

  const rates = d.rates ?? null;
  const charge = d.charge ?? null;
  const cost = d.cost ?? null;
  const usage = d.usage ?? {};
  const warnings = arr(d.warnings);
  /** Statement made before rate table v70 (revision 8) — charge/margin null, old usage shape */
  const legacy = isLegacyStatementDetail(d);
  /** The month has a default rate, so the sale amounts were computed */
  const priced = !!rates && !!charge && !legacy;
  /** No default rate, but a human entered amounts (reply table 16) */
  const manualOnly = !rates && !!charge;
  const internal = isInternalDetail(d);
  const drift = warnings.some((w) => w?.code === 'STATEMENT_DRIFT');
  /** Statement items (`statement.lines` — amount · estimate · overridden · memo per item, 요청 계약 4-3) */
  const stLines = d.statement?.lines ?? null;
  const lines = charge?.lines ?? {};
  const dim = daysInMonth(month);
  /** Before the billing start date — nothing is billed (charge/margin null) */
  const notBillable = isNotBillableDetail(d);
  /** Start month (start date not the 1st) — billed cost (cost.billable) vs. cost before the start (cost.pre_start) */
  const billable = billableCostOf(d);
  const startMonth = billable.split && !notBillable;

  const sourceKey = charge?.source;
  const sourceLabel = sourceKey ? SOURCE_TEXT[sourceKey] ?? chargeSourceLabel(sourceKey) : '';
  const sourceTone = sourceKey === 'estimate' ? 'muted' : 'accent';

  /** The month's default version is in the pre-v70 shape (DEFAULT_PLAN_MISSING `legacy_version`) */
  const legacyVersion = legacyPlanVersionOf(warnings);
  const planMissingText = legacyVersion
    ? `그 달에 적용되는 기본 요율 ${legacyVersion} 버전이 요금표 v70 이전 모양이라 금액을 계산하지 않았습니다. 그 버전을 새 칸으로 다시 저장한 뒤 [다시 계산]하세요.`
    : warningMeta('DEFAULT_PLAN_MISSING').description;
  const planMissingLinkText = legacyVersion ? '기본 요율 화면에서 다시 저장하기 →' : '기본 요율 화면에서 버전 만들기 →';
  /** A confirmed month has no [다시 계산] — it is reopened first */
  const status = statementStatusOf(d);
  const legacyNext = status === 'confirmed' ? '[재수정] → [다시 계산]' : '[다시 계산]';
  const legacyText = `요금표 v70 이전 정산서라 서티 청구 항목별 금액과 마진이 없습니다. ${legacyNext}한 뒤에 보입니다. 원가는 요율과 상관없어 그대로 보입니다. 다시 계산하기 전에는 저장·확정·일회성 항목 쓰기를 할 수 없습니다. ${legacyStatementActionText(status)}`;

  // -------------------------------------------------------------------------
  // Cells — cost always shows (vox cost does not depend on rates); sale unit / amount / margin need rates
  // -------------------------------------------------------------------------

  /** vox cost (real) or gray reference (PBX trunk / STT estimates) */
  function costCell(value, { ref = false, note = '' } = {}) {
    return wonCell(value, { ref, note, negative: isNegative(value) });
  }

  function saleUnitCell(text, source = null, { missing = false, muted = false } = {}) {
    if (!priced) return NONE;
    return textCell(text, { source: source === 'default' || source === 'override' ? source : null, missing, muted });
  }

  function saleCell(value, { missing = false, note = '' } = {}) {
    if (!priced) return NONE;
    // Internal/test companies are billed 0원 (nothing to bill even with an empty sale price)
    if (internal) return wonCell(0);
    if (missing) return textCell('미정', { missing: true });
    return wonCell(value, { note });
  }

  function marginCell(value, { missing = false, ref = false, note = '' } = {}) {
    if (!priced || internal) return NONE;
    if (missing) return textCell('미정', { missing: true, ref });
    return wonCell(value, { ref, note: isNumber(value) ? note : '', negative: isNegative(value) });
  }

  /** Margin read on the total only (base and call items — the cost cannot be split per item) */
  function totalOnlyMargin() {
    if (!priced || internal) return NONE;
    return textCell(EMPTY, { note: '합계에서', muted: true });
  }

  /**
   * Is the item amount a human-entered one (lineOverride)? A live estimate (`charge.source: estimate`) is always the
   * server's computed value. Internal/test companies are billed 0원.
   * @param {'base'|'inbound'|'outbound'|'web_text'|'line'|'counselor_number'} key
   * @returns {{ overridden: true, estimate: number|null, memo: string }|null}
   */
  function overrideOf(key) {
    if (!charge || internal || legacy || charge.source === 'estimate') return null;
    const line = lines[key];
    // Without a default rate there is no computed value — only the statement item's `overridden` counts
    const computedValue = priced ? numOr(line?.estimate) : null;
    const ov = lineOverride(key, { amount: numOr(line?.amount), computed: computedValue, statementLines: stLines });
    return ov.overridden ? ov : null;
  }

  /** Secondary text of a human-entered amount — the computed value instead of the calculation */
  function overrideNote(ov) {
    return isNumber(ov.estimate) ? `직접 입력 · 계산값 ${formatWon(ov.estimate)}` : '직접 입력';
  }

  /**
   * Sale amount cell of an item — saleCell when computed; a manual-only month shows the human amounts only (its server 0
   * means "not computed", so it is never written as '계산값 0원').
   */
  function groupSaleCell(amount, override, note) {
    if (manualOnly) {
      if (internal) return wonCell(0);
      return override ? wonCell(amount, { note: '직접 입력 · 계산값 없음' }) : NONE;
    }
    return saleCell(amount, { note: override ? overrideNote(override) : note });
  }

  /** Section head (서티 청구 / 원가) */
  function section(area, name, sub = '') {
    return { key: `section.${area}`, kind: 'section', area, level: null, name, sub };
  }

  /** Basis row — one text across the value columns */
  function detailRow(key, name, text, { sub = '' } = {}) {
    return { key, kind: 'detail', area: 'sale', level: 1, name, sub, detail: textCell(text || EMPTY) };
  }

  /**
   * Calculation basis row. When the billed amount is human-entered, the row is the computed value's basis — not the
   * basis of the billed amount (lineOverride rule).
   */
  function calcRow(key, text, sub, override) {
    if (!override) return detailRow(key, '계산', text, { sub });
    return detailRow(key, '계산값 근거', `${text} (계산값, 청구 금액은 직접 입력)`, { sub });
  }

  /** Item row (level 0 group or level 1 child) */
  function itemRow(area, key, level, name, sub, cells) {
    return {
      key,
      kind: 'item',
      area,
      level,
      name,
      sub,
      usage: BLANK,
      costUnit: BLANK,
      cost: BLANK,
      saleUnit: BLANK,
      sale: BLANK,
      margin: BLANK,
      ...cells,
    };
  }

  /** Child row with only a usage text (other cells blank unless given) */
  function usageRow(area, key, name, sub, usageText, extra = {}) {
    return itemRow(area, key, 1, name, sub, { usage: textCell(usageText), ...extra });
  }

  // -------------------------------------------------------------------------
  // 서티 청구
  // -------------------------------------------------------------------------

  /** `charge.lines.base.start_month` (start month, not the 1st) — legacy statements have none */
  function startMonthOf(b) {
    return legacy ? null : obj(b?.start_month);
  }

  /** ' = 120분 × 21/30일' — start month wins over the opening month */
  function includedFormulaText(base, sm, smWindow, minutes) {
    if (!isNumber(minutes)) return '';
    if (smWindow) return ` = ${formatNumber(minutes)}분 × ${formatNumber(sm.days)}/${formatNumber(sm.month_days)}일 · 시작일부터`;
    if (base?.opening_month) return ` = ${formatNumber(minutes)}분 × ${formatNumber(base.days)}/${formatNumber(base.days_in_month)}일`;
    return '';
  }

  /**
   * Counts and seconds next to the billed time — billed fields in a start month (charge.lines.inbound.phone_calls… — contract
   * 1-2 [4단계 확인]); '(달 전체)' when a start month lacks them. Same rule as the statement sheet.
   */
  function inboundCountsOf(i, ui, wv) {
    const split = !!startMonthOf(lines.base) || !!obj(usage.pre_start);
    const full = { phoneCalls: numOr(ui?.calls), ai: numOr(ui?.ai_seconds), counselor: numOr(ui?.counselor_talk_seconds), webCalls: numOr(wv?.calls) };
    if (!split) return { ...full, fullTag: '', webFullTag: '' };
    const billedPhone = isNumber(i?.phone_calls);
    const billedWeb = isNumber(i?.web_voice_calls);
    return {
      phoneCalls: billedPhone ? i.phone_calls : full.phoneCalls,
      ai: billedPhone ? numOr(i?.phone_ai_seconds) : full.ai,
      counselor: billedPhone ? numOr(i?.phone_counselor_talk_seconds) : full.counselor,
      webCalls: billedWeb ? i.web_voice_calls : full.webCalls,
      fullTag: billedPhone ? '' : ' (달 전체)',
      webFullTag: billedWeb ? '' : '(달 전체)',
    };
  }

  function baseRows() {
    const b = obj(lines.base);
    const amount = numOr(b?.amount);
    const override = overrideOf('base');
    const fee = numOr(b?.monthly_vat_included);
    const inc = obj(b?.included_line);
    // Start month (not the 1st) — prorated from the start date; wins over the opening-month wording (plan 4-3)
    const sm = startMonthOf(b);
    const smWindow = startMonthWindowText(sm);
    let usageText = EMPTY;
    if (b && priced) {
      if (smWindow) usageText = smWindow;
      else if (b.opening_month) usageText = `개통 달 ${formatNumber(b.days)}일 / ${formatNumber(b.days_in_month)}일`;
      else if (inc) usageText = `포함 회선 ${maskNumber(inc.number_last4)} · ${formatNumber(inc.days)}일`;
      else usageText = b.warning === 'NO_PHONE_USAGE' ? '포함 회선·전화 없음' : '포함 회선 없음';
      if ((smWindow || b.opening_month) && inc) usageText += ` · 포함 회선 ${maskNumber(inc.number_last4)}`;
    }
    let note = '';
    if (!override && b && isNumber(fee)) {
      if (smWindow) note = `⌊${formatNumber(fee)} × 10 × ${formatNumber(sm.days)} ÷ (11 × ${formatNumber(sm.month_days)})⌋ · 시작일부터`;
      else
        note = b.opening_month
          ? `⌊${formatNumber(fee)} × 10 × ${formatNumber(b.days)} ÷ (11 × ${formatNumber(b.days_in_month)})⌋`
          : `${formatNumber(fee)} × 10 ÷ 11`;
    }
    return [
      {
        ...itemRow('sale', 'base', 0, '월 사용료', baseIncludesText(rates), {
          usage: textCell(usageText),
          cost: textCell(EMPTY, { note: '통화·회선 원가는 아래 원가 항목', muted: true }),
          saleUnit: saleUnitCell(isNumber(fee) ? `월 ${formatNumber(fee)}원(VAT 포함)` : '미정', b?.rate_source, {
            missing: !isNumber(fee),
          }),
          sale: groupSaleCell(amount, override, note),
          margin: totalOnlyMargin(),
        }),
        warning: priced ? b?.warning ?? null : null,
        override,
      },
    ];
  }

  function inboundRows() {
    const i = obj(lines.inbound);
    const ui = obj(usage.inbound);
    const wv = obj(usage.web_voice);
    const amount = numOr(i?.amount);
    const override = overrideOf('inbound');
    const phone = numOr(i?.phone_units_10s) ?? numOr(ui?.units_10s);
    const web = numOr(i?.web_voice_units_10s) ?? numOr(wv?.units_10s);
    const used = numOr(i?.used_units_10s) ?? (phone !== null ? phone + (web ?? 0) : null);
    const included = numOr(i?.included_units_10s);
    const overage = numOr(i?.overage_units_10s);
    const price = numOr(i?.price_per_minute);
    const minutes = numOr(i?.included_minutes);
    const base = obj(lines.base);
    const sm = startMonthOf(base);
    const smWindow = startMonthWindowText(sm);
    const counts = inboundCountsOf(i, ui, wv);
    const rows = [
      {
        ...itemRow('sale', 'inbound', 0, '120분 초과 통화', '수신 통화 + 웹 음성 − 포함 통화 · 통화마다 10초 올림', {
          usage: textCell(
            priced && isNumber(included)
              ? `사용 ${timeOf(used)} / 포함 ${timeOf(included)} · 초과 ${timeOf(overage)}`
              : `사용 ${timeOf(used)}`
          ),
          cost: textCell(EMPTY, { note: 'AI 음성 원가는 아래 원가 항목', muted: true }),
          saleUnit: saleUnitCell(isNumber(price) ? `1분당 ${formatUnitPrice(price)}원` : '미정', i?.price_per_minute_rate_source, {
            missing: !isNumber(price),
          }),
          sale: groupSaleCell(amount, override, ''),
          margin: totalOnlyMargin(),
        }),
        override,
      },
    ];
    // Unknown usage (old shape) — no child rows to draw
    if (!ui && !i) return rows;
    rows.push(
      usageRow(
        'sale',
        'inbound.phone',
        '전화(수신)',
        '통화마다 AI + 상담사 통화를 더해 10초 올림',
        [
          timeOf(phone),
          isNumber(counts.phoneCalls) ? `수신 ${formatNumber(counts.phoneCalls)}통` : '',
          isNumber(counts.ai) ? `AI ${formatDuration(counts.ai)}` : '',
          isNumber(counts.counselor) ? `상담사 ${formatDuration(counts.counselor)}` : '',
        ]
          .filter(Boolean)
          .join(' · ')
          .concat(counts.fullTag)
      ),
      usageRow(
        'sale',
        'inbound.web_voice',
        '웹 음성',
        'studio 웹 테스트 음성 · 통화마다 10초 올림',
        [timeOf(web), isNumber(counts.webCalls) ? `웹 음성 ${formatNumber(counts.webCalls)}통${counts.webFullTag}` : '']
          .filter(Boolean)
          .join(' · '),
        { webNotice: webUsageNotice(d) }
      )
    );
    if (priced) {
      rows.push(
        usageRow(
          'sale',
          'inbound.included',
          '포함 통화',
          smWindow ? '시작일부터 일할해 올림' : base?.opening_month ? '개통 달 — 사용 일수로 일할해 올림' : '월 사용료에 포함',
          isNumber(included) ? `${timeOf(included)}${includedFormulaText(base, sm, smWindow, minutes)}` : EMPTY,
          {
            saleUnit: saleUnitCell(isNumber(minutes) ? `${formatNumber(minutes)}분` : '미정', i?.included_minutes_rate_source, {
              missing: !isNumber(minutes),
            }),
          }
        )
      );
      if (!internal && isNumber(overage) && isNumber(price)) {
        rows.push(calcRow('inbound.calc', overageFormulaText(overage, price, i?.overage_amount), '초과 시간 × 1분당 단가', override));
      }
    }
    return rows;
  }

  function outboundRows() {
    const o = obj(lines.outbound);
    const uo = obj(usage.outbound);
    const amount = numOr(o?.amount);
    const override = overrideOf('outbound');
    // Legacy usage has no outbound block — only the count from `usage.calls.outbound`
    const calls = numOr(uo?.calls) ?? numOr(usage.calls?.outbound);
    const dialed = numOr(o?.dialed) ?? numOr(uo?.dialed);
    const answered = numOr(o?.answered) ?? numOr(uo?.answered);
    const units = numOr(o?.units_10s) ?? numOr(uo?.units_10s);
    const none = !calls && !dialed;
    const counts = outboundCountText({ calls, dialed });
    let usageText = '발신 없음';
    if (!none) {
      usageText = [
        counts.total,
        isNumber(answered) ? `받은 통화 ${formatNumber(answered)}통` : '',
        isNumber(units) ? timeOf(units) : '',
      ]
        .filter(Boolean)
        .join(' · ');
    }
    const rows = [
      {
        ...itemRow('sale', 'outbound', 0, '발신', '건당 + 받은 통화 · 120분과 따로', {
          usage: textCell(usageText),
          cost: textCell(EMPTY, { note: none ? '' : 'AI 음성 원가는 아래 원가 항목', muted: true }),
          saleUnit: saleUnitCell(none ? '' : '아래 두 항목 합'),
          sale: groupSaleCell(amount, override, ''),
          margin: totalOnlyMargin(),
        }),
        override,
      },
    ];
    if (none || (!o && !uo)) return rows;
    const perCall = numOr(o?.per_call);
    const unitPrice = numOr(o?.unit_price);
    rows.push(
      usageRow(
        'sale',
        'outbound.per_call',
        '발신 건당',
        '실제로 건 통화(무응답·음성사서함 포함) · 걸기 전 취소·발신 실패 0원',
        counts.perCall,
        {
          saleUnit: saleUnitCell(isNumber(perCall) ? `${formatUnitPrice(perCall)}원/건` : '미정', o?.per_call_rate_source, {
            missing: !isNumber(perCall),
          }),
          sale: saleCell(o?.calls_amount, {
            note: isNumber(dialed) && isNumber(perCall) ? `${formatNumber(dialed)}건 × 건당 ${formatUnitPrice(perCall)}원` : '',
          }),
        }
      ),
      usageRow(
        'sale',
        'outbound.answered',
        '받은 발신 통화',
        '환자가 받은 통화만 · 통화마다 AI + 상담사 통화를 더해 10초 올림',
        [`${formatNumber(answered)}통`, timeOf(units), isNumber(uo?.ai_seconds) ? `AI ${formatDuration(uo.ai_seconds)}` : '']
          .filter(Boolean)
          .join(' · '),
        {
          saleUnit: saleUnitCell(isNumber(unitPrice) ? `10초당 ${formatUnitPrice(unitPrice)}원` : '미정', o?.unit_price_rate_source, {
            missing: !isNumber(unitPrice),
          }),
          sale: saleCell(o?.talk_amount, {
            note: isNumber(units) && isNumber(unitPrice) ? `${timeOf(units)} × 10초당 ${formatUnitPrice(unitPrice)}원` : '',
          }),
        }
      )
    );
    return rows;
  }

  function webTextRow() {
    const w = obj(lines.web_text);
    const uw = obj(usage.web_text);
    const amount = numOr(w?.amount);
    const override = overrideOf('web_text');
    const webCost = numOr(cost?.split?.web_text) ?? numOr(uw?.vox_cost);
    const passThrough = typeof w?.pass_through === 'boolean' ? w.pass_through : null;
    // Start month — the billed amount covers the web text after the start date only
    const marginCost = startMonth ? numOr(cost.billable?.split?.web_text) : webCost;
    const billedCalls = numOr(w?.calls) ?? numOr(uw?.calls);
    // Start month — the cost cell is the whole month (same population as the cost items), so the usage is too; the billed
    // count and its cost (the margin basis) are named next to it
    const fullCalls = numOr(uw?.calls);
    const usageText =
      startMonth && isNumber(fullCalls)
        ? `웹 텍스트 ${formatNumber(fullCalls)}통(달 전체) · 청구분 ${formatNumber(billedCalls)}통`
        : `웹 텍스트 ${formatNumber(billedCalls)}통`;
    return {
      ...itemRow('sale', 'web_text', 0, '웹 텍스트', 'studio 웹 테스트 · 메시지당 vox 요금', {
        usage: textCell(usageText),
        costUnit: textCell('vox 메시지 요금'),
        cost: costCell(webCost, { note: startMonth ? '달 전체' : '' }),
        saleUnit: saleUnitCell(passThrough === false ? '청구 안 함' : passThrough === true ? '원가 그대로' : '미정', w?.rate_source, {
          missing: passThrough === null,
        }),
        sale: groupSaleCell(amount, override, ''),
        margin: marginCell(minus(amount, marginCost), {
          note: startMonth ? (isNumber(marginCost) ? `청구분 원가 ${formatWon(marginCost)} 기준` : '청구분 원가 기준') : '',
        }),
      }),
      override,
      webNotice: webUsageNotice(d),
    };
  }

  /**
   * 회선 — v70 정산서는 서티 청구에, v70 이전 정산서는 원가에 놓인다(area).
   * @param {'sale'|'cost'} area
   */
  function lineRows(area) {
    const used = arr(usage.lines);
    const l = obj(lines.line);
    const lineCost = numOr(cost?.line);
    const amount = numOr(l?.amount);
    const override = overrideOf('line');
    // Server amount per line (`charge.lines.line.items[]`)
    const items = priced ? arr(l?.items) : [];
    const rows = pairLines(used, items).map(({ u, item }, idx) => {
      const included = item?.included === true;
      const price = numOr(item?.monthly);
      const missing = !!item && !included && price === null;
      const lineAmount = numOr(item?.amount);
      let unitText = EMPTY;
      if (item) {
        if (included) unitText = '포함';
        else if (missing) unitText = '미정';
        else unitText = `월 ${formatNumber(price)}원${item.prorate === true ? ' · 일할' : ''}`;
      }
      let sub = '월정액 일할 추정';
      if (included) sub = '포함 회선 · 월 사용료에 포함';
      else if (!u) sub = '이 달 회선 사용 기록 없음 · 청구 금액만';
      else if (u.source === 'invoice') sub = 'vox 청구서 회선 금액';
      let marginValue = NONE;
      if (item) {
        // Start month — the line cost per line is the whole month (no billed split per line), so read it on the total
        if (!included && startMonth && !missing) marginValue = totalOnlyMargin();
        else if (!included) marginValue = marginCell(minus(lineAmount, numOr(u?.amount)), { missing });
        else if (priced && !internal) marginValue = textCell(EMPTY, { note: '월 사용료에 포함', muted: true });
      }
      return {
        ...itemRow(
          area,
          `line.${idx}`,
          1,
          `${lineTypeLabel(u?.type ?? item?.type)} 회선 ${maskNumber(u?.number_last4 ?? item?.number_last4)}`,
          sub,
          {
            usage: textCell(isNumber(u?.days) && dim ? `${formatNumber(u.days)}일 / ${dim}일` : EMPTY),
            costUnit: textCell(isNumber(u?.monthly_fee) ? `월 ${formatNumber(u.monthly_fee)}원` : EMPTY),
            cost: u ? costCell(numOr(u.amount)) : NONE,
            saleUnit: item ? saleUnitCell(unitText, item.rate_source, { missing, muted: included }) : NONE,
            // Start month — the cost (usage cell) is the whole month; the sale is prorated on the billed days
            sale: item
              ? saleCell(lineAmount, {
                  missing,
                  note: startMonth && !included && isNumber(item.days) && dim ? `청구 ${formatNumber(item.days)}일 / ${dim}일` : '',
                })
              : NONE,
            margin: marginValue,
          }
        ),
        warning: priced ? item?.warning ?? null : null,
      };
    });
    // A human-entered amount is not "computed without the unset lines"
    const partial = priced && !internal && rows.some((r) => r.warning === 'LINE_RATE_UNSET') && !override;
    const extra = items.filter((it) => it?.included !== true).length;
    const count = rows.length;
    let usageText = '';
    if (count) {
      usageText = priced ? `${formatNumber(count)}개 · 추가·대표번호 ${formatNumber(extra)}개` : `${formatNumber(count)}개`;
    }
    let sub = '이 달 회선 없음';
    if (count) sub = legacy ? 'vox 회선 원가' : '070 1회선은 월 사용료에 포함 · 추가 회선·대표번호는 개통·해지 달 일할';
    return [
      {
        ...itemRow(area, 'line', 0, '회선', sub, {
          usage: textCell(usageText),
          cost: costCell(lineCost, { note: count && !legacy ? '포함 회선 원가 포함' : '' }),
          saleUnit: saleUnitCell(count ? '아래 항목 합' : ''),
          sale: groupSaleCell(amount, override, ''),
          margin: totalOnlyMargin(),
        }),
        partial,
        partialTip: '판매가가 미정인 회선은 금액에서 뺐습니다(LINE_RATE_UNSET).',
        override,
      },
      ...rows,
    ];
  }

  /**
   * 상담사 번호 추가 (번호 요청 A7) — basis texts from settlementRules.counselorNumberLineView. Amounts are server values
   * only; segments carry no amounts. No cost — the margin column is the item amount. v70-legacy statements have no data.
   */
  function counselorNumberRows() {
    if (legacy) return [];
    const v = counselorNumberLineView(withNames ? d : withoutPeakNames(d));
    const line = obj(lines.counselor_number);
    const override = v.state === 'unpriced' ? null : overrideOf('counselor_number');
    // Items with evidence (segments, peak day) — priced, or 0원 because of no calls
    const evidence = v.state === 'applies' || v.state === 'no_calls';
    let usageText = EMPTY;
    if (priced && isNumber(line?.peak_count) && (evidence || line.peak_count > 0)) {
      const extraCount = numOr(line?.peak_extra) ?? 0;
      usageText = `이 달 최대 ${formatNumber(line.peak_count)}개${extraCount > 0 ? ` · 초과 ${formatNumber(extraCount)}개` : ''}`;
    }
    let saleUnit = saleUnitCell(EMPTY);
    if (evidence && v.unitText) saleUnit = saleUnitCell(v.unitText, v.rateSource);
    else if (v.state === 'no_rate') saleUnit = saleUnitCell(COUNSELOR_LINE_NO_RATE_TEXT, null, { muted: true });
    const rows = [
      {
        ...itemRow('sale', 'counselor_number', 0, '상담사 번호 추가', COUNSELOR_LINE_COST_TEXT, {
          usage: textCell(usageText),
          cost: wonCell(0, { note: '원가 없음', muted: true }),
          saleUnit,
          sale: groupSaleCell(v.amount, override, ''),
          margin: marginCell(v.amount),
        }),
        warning: priced ? v.warning : null,
        override,
      },
    ];
    if (!priced) return rows;
    if (v.segmentsText) {
      rows.push(detailRow('counselor_number.segments', '구간', v.segmentsText, { sub: '그날 번호 개수 · 기본 포함 수를 넘은 수' }));
    }
    if (v.formulaText && !internal) {
      rows.push(calcRow('counselor_number.formula', v.formulaText, '초과 개수 × 일수 ÷ 그 달 일수 × 1개당 월 금액', override));
    }
    if (v.windowText) {
      rows.push(
        detailRow('counselor_number.window', '청구 창', v.windowText, { sub: '개통 달 — 포함 070 회선이 켜진 날부터 그 달 끝' })
      );
    }
    if (v.gapText) {
      rows.push(
        detailRow('counselor_number.gaps', '매시 기록 없는 날', v.gapText, {
          sub: '그날 쓴 값은 번호를 추가할 때의 기록, 없으면 0 · 앞뒤 기록은 참고값',
        })
      );
    }
    if (v.peakText) {
      rows.push(
        detailRow('counselor_number.peak', '가장 많았던 날', v.peakText, {
          sub: withNames ? '그날 번호 이름 · 끝 4자리' : '그날 번호 개수',
        })
      );
    }
    // no_rate already says it in the sale unit cell
    if (v.noteText && v.state !== 'no_rate') rows.push(detailRow('counselor_number.note', '안내', v.noteText));
    return rows;
  }

  function oneOffRows() {
    const oneOff = charge?.lines?.one_off ?? d.statement?.lines?.one_off ?? null;
    const items = arr(oneOff?.items);
    // One-off amounts do not depend on rates — they show on manual-only months and v70-legacy statements too
    const oneOffSale = (value) => (manualOnly || legacy ? wonCell(internal ? 0 : value) : saleCell(value));
    return [
      itemRow('sale', 'one_off', 0, '일회성 항목', '단말기 등 · 원가 없음 · 마진 계산 제외', {
        usage: textCell(items.length ? `${formatNumber(items.length)}건` : '없음'),
        cost: textCell('원가 없음', { muted: true }),
        sale: oneOffSale(numOr(oneOff?.amount) ?? oneOffAmountOf(d)),
        margin: NONE,
      }),
      ...items.map((it, i) =>
        itemRow('sale', `one_off.${it?.item_id ?? i}`, 1, it?.name || EMPTY, it?.memo || '', {
          cost: NONE,
          sale: oneOffSale(numOr(it?.amount)),
          margin: NONE,
        })
      ),
    ];
  }

  // -------------------------------------------------------------------------
  // 원가 (vox cost that cannot be split per billed item)
  // -------------------------------------------------------------------------

  /** Sale unit cell of a cost row — which billed item covers it */
  function coveredBy(text) {
    return priced ? textCell(text, { muted: true }) : NONE;
  }

  function aiCostRow() {
    const u = usage;
    const unitCost = numOr(rates?.ai?.cost_per_unit);
    const unitSec = numOr(rates?.ai?.unit_sec) ?? 10;
    const calls = aiTimeCalls(d);
    const aiUnits = numOr(u.ai?.units_10s);
    const webVoice = numOr(u.web_voice?.units_10s);
    return itemRow('cost', 'cost.ai', 0, 'AI 음성', '전화(수신·발신)·웹 음성 · 통화마다 AI 만 10초 올림(원가 기준)', {
      usage: textCell(
        [
          `AI ${formatDuration(u.ai?.seconds)}`,
          aiUnits !== null ? `올림 ${formatDuration(aiUnits * 10)}` : '',
          webVoice ? `웹 음성 ${formatDuration(webVoice * 10)}` : '',
          isNumber(calls.count) ? `${formatNumber(calls.count)}통(${calls.scope})` : '',
        ]
          .filter(Boolean)
          .join(' · ')
      ),
      costUnit: textCell(isNumber(unitCost) ? `${formatUnitPrice(unitCost)}원/${unitSec}초` : EMPTY),
      cost: costCell(numOr(cost?.split?.voice_ai)),
      saleUnit: coveredBy('월 사용료·120분 초과·발신 항목'),
    });
  }

  function counselorCostRows() {
    const u = obj(usage.counselor) ?? {};
    const legs = arr(u.legs);
    const split = cost?.split ?? {};
    const hasVox = legs.some((l) => l?.route === 'vox_direct');
    const hasPbx = legs.some((l) => l?.route === 'pbx' && (l.answered ?? 0) > 0);
    const splitKnown = isNumber(split.vox_direct_transfer) || isNumber(split.vox_direct_telecom);
    const voxCost = !hasVox
      ? 0
      : splitKnown
        ? round2((numOr(split.vox_direct_transfer) ?? 0) + (numOr(split.vox_direct_telecom) ?? 0))
        : null;
    const segment = numOr(u.segment_seconds);
    const mode = connectModeLabel(detailConnectMode(d).mode);
    const rows = [
      itemRow('cost', 'cost.counselor', 0, '상담사 연결', mode, {
        usage: textCell(
          `응답 ${formatNumber(u.answered)}통 / 요청 ${formatNumber(u.requested)}통 · 상담사 통화 ${formatDuration(u.talk_seconds)}`
        ),
        cost:
          voxCost === null
            ? textCell('기타 원가에 포함', { muted: true })
            : costCell(voxCost, { note: hasVox ? 'vox 직접 전환료·통신료' : 'PBX 경로 · vox 원가 없음' }),
        saleUnit: coveredBy('월 사용료에 포함 · 판매가 없음'),
      }),
    ];
    // Connection segment time and per route × number type answers / talk time (the old review form's basis panel)
    if (segment !== null) {
      rows.push(usageRow('cost', 'cost.counselor.segment', '연결 구간', '상담사 연결 구간 시간 합(vox 기준)', formatDuration(segment)));
    }
    legs.forEach((leg, idx) => {
      const talk = numOr(leg?.talk_seconds);
      rows.push(
        usageRow(
          'cost',
          `cost.counselor.leg.${idx}`,
          counselorItemLabel(leg),
          '경로 → 번호 종류',
          `응답 ${formatNumber(leg?.answered)}통${talk === null ? '' : ` · 통화 ${formatDuration(talk)}`}`
        )
      );
    });
    if (hasVox && splitKnown) {
      rows.push(
        usageRow('cost', 'cost.counselor.transfer', 'vox 직접 전환', '상담사에게 넘긴 구간', '', {
          cost: costCell(numOr(split.vox_direct_transfer)),
        }),
        usageRow('cost', 'cost.counselor.telecom', 'vox 직접 통신료', '번호 종류별 통신료', '', {
          cost: costCell(numOr(split.vox_direct_telecom)),
        })
      );
    }
    if (hasPbx) {
      const pbxLegs = legs.filter((l) => l?.route === 'pbx' && (l.answered ?? 0) > 0);
      const pbxAnswered = pbxLegs.reduce((acc, l) => acc + (numOr(l.answered) ?? 0), 0);
      const pbxTalk = pbxLegs.reduce((acc, l) => acc + (numOr(l.talk_seconds) ?? 0), 0);
      const trunk = numOr(cost?.pbx_trunk_estimate);
      const stt = numOr(cost?.stt_estimate);
      rows.push(
        usageRow(
          'cost',
          'cost.counselor.pbx',
          'PBX 트렁크',
          '세종 트렁크 추정 · vox 원가 아님',
          `응답 ${formatNumber(pbxAnswered)}통 · 통화 ${formatDuration(pbxTalk)}`,
          {
            costUnit: textCell('', { muted: true }),
            cost: trunk === null ? NONE : costCell(trunk, { ref: true, note: '합계 제외' }),
          }
        ),
        usageRow(
          'cost',
          'cost.counselor.stt',
          'PBX 통화 전환료(STT)',
          '리턴제로 추정 · 상담사 통화 시간 × 2',
          `${formatDuration(pbxTalk)} × 2 = ${formatDuration(pbxTalk * 2)}`,
          {
            costUnit: textCell('', { muted: true }),
            cost: stt === null ? NONE : costCell(stt, { ref: true, note: '합계 제외' }),
          }
        )
      );
    }
    return rows;
  }

  function otherRow() {
    const other = numOr(cost?.split?.other);
    const cold = warnings.find((w) => w?.code === 'VOX_DIRECT_COLD_UNMEASURED');
    const split = cost?.split ?? {};
    const voxUnsplit =
      arr(usage.counselor?.legs).some((l) => l?.route === 'vox_direct') &&
      !isNumber(split.vox_direct_transfer) &&
      !isNumber(split.vox_direct_telecom);
    let usageText = '';
    if (cold) {
      // Without a count, the request wording ("셀 수 없음 — 기타 원가로 판단") is read for this row
      usageText = isNumber(cold.count)
        ? `vox 직접 cold 전환 ${formatNumber(cold.count)}건 포함`
        : 'vox 직접 cold 전환 — 셀 수 없음 — 이 원가로 판단';
    } else if (voxUnsplit) {
      usageText = 'vox 직접 전환료·통신료 포함';
    }
    return itemRow('cost', 'other', 0, '기타 원가', '위 항목으로 나뉘지 않은 vox 원가(cold 전환·발신 기본료·부가 항목 등)', {
      usage: textCell(usageText),
      cost: costCell(other),
      saleUnit: coveredBy('청구하지 않음'),
    });
  }

  /** Start month — vox cost before the start date (ours, not billed) — a reference row outside the total */
  function preStartCostRow() {
    const startDate = settlementScopeView(d).startDate;
    const short = shortDateText(startDate);
    return itemRow('cost', 'cost.pre_start', 0, PRE_START_COST_LABEL, `정산 시작${short ? ` ${short}` : '일'} 전 · 청구하지 않음`, {
      usage: textCell(preStartUsageShortText(usage.pre_start)),
      costUnit: NONE,
      cost: costCell(billable.preStart, { ref: true, note: '합계에서 뺌' }),
      saleUnit: BLANK,
      sale: BLANK,
      margin: BLANK,
    });
  }

  let rows;
  if (legacy) {
    // v70-legacy statement (revision 8) — old item/rate/usage shapes: no billed items, only one-off items and costs
    rows = [
      section('sale', '서티 청구', `v70 이전 정산서 — 항목별 금액은 ${legacyNext}한 뒤에 보입니다`),
      ...oneOffRows(),
      section('cost', '원가', 'vox 원가 · 회선 원가 포함'),
      aiCostRow(),
      ...counselorCostRows(),
      ...lineRows('cost'),
      otherRow(),
    ];
  } else {
    rows = [
      section('sale', '서티 청구', '청구 일곱 항목 — 월 사용료 · 120분 초과 통화 · 발신 · 웹 텍스트 · 회선 · 상담사 번호 추가 · 일회성'),
      ...baseRows(),
      ...inboundRows(),
      ...outboundRows(),
      webTextRow(),
      ...lineRows('sale'),
      ...counselorNumberRows(),
      ...oneOffRows(),
      section('cost', '원가', '서티 청구 항목과 나눌 수 없는 vox 원가 · 웹 텍스트·회선 원가는 위 그 항목에'),
      aiCostRow(),
      ...counselorCostRows(),
      otherRow(),
      ...(startMonth ? [preStartCostRow()] : []),
    ];
  }
  if (notBillable) {
    // Nothing is billed — sale unit / sale / margin cells stay empty (cost rows as they are)
    rows = rows.map((r) => (r.kind === 'item' ? { ...r, saleUnit: BLANK, sale: BLANK, margin: BLANK, warning: null, override: null, partial: false } : r));
  }

  // -------------------------------------------------------------------------
  // Totals — the review input in progress wins over the snapshot
  // -------------------------------------------------------------------------

  const supply = live ? live.supply : summary.supply.amount;
  const vat = live ? vatOf(live.supply) : summary.vat;
  let totalWithVat = summary.totalWithVat;
  if (live) totalWithVat = isNumber(supply) && isNumber(vat) ? supply + vat : null;
  const margin = live ? live.margin : summary.margin;

  const partialList = summary.supply.partialLines;
  const partialTip = partialList?.length
    ? `판매가 미정이라 뺀 항목: ${partialList.join(', ')}`
    : '판매가 미정이라 뺀 항목이 있습니다.';

  /** Usage cell of the total row — what 공급가액 · vox 원가 · 마진 add up */
  const notes = [];
  // The snapshot breakdown no longer matches while amounts are being typed
  if (summary.supply.breakdown && !internal && !live) notes.push(`공급가액 = ${summary.supply.breakdown}`);
  if (summary.cost.breakdown) notes.push(`vox 원가 = ${summary.cost.breakdown}`);
  if (startMonth && isNumber(billable.fullTotal) && isNumber(billable.preStart)) {
    notes.push(`vox 원가(청구분) = 달 전체 ${formatNumber(billable.fullTotal)} − 시작 전 ${formatNumber(billable.preStart)}`);
  }
  if (legacy) {
    notes.push(`공급가액은 그때 저장한 정산서 금액 · 마진은 ${legacyNext}한 뒤에 보입니다`);
  } else if (isNumber(margin) && isNumber(supply) && isNumber(summary.cost.total)) {
    const oneOff = oneOffAmountOf(d);
    notes.push(`마진 = 공급가액(일회성 제외) ${formatNumber(supply - oneOff)} − vox 원가 ${formatNumber(summary.cost.total)}`);
  } else {
    notes.push('마진 = 공급가액(일회성 항목 제외) − vox 원가');
  }

  /** Gray reference below the table — margin after the PBX trunk estimate (and the STT estimate) */
  let referenceMarginText = '';
  const trunk = numOr(cost?.pbx_trunk_estimate);
  if (!live && isNumber(summary.margin) && isNumber(summary.marginInclPbxTrunk) && trunk) {
    const parts = [`참고 · PBX 트렁크 추정 ${formatWon(trunk)} 포함 시 마진 ${formatWon(summary.marginInclPbxTrunk)}`];
    const stt = numOr(cost?.stt_estimate);
    if (stt) parts.push(`STT 원가 추정 ${formatWon(stt)}까지 빼면 약 ${formatWon(summary.marginInclPbxTrunk - stt)}`);
    referenceMarginText = parts.join(' · ');
  }
  // Start month — the margin if the cost before the start date (ours) were counted too (whole-month cost basis)
  if (startMonth && isNumber(margin) && isNumber(billable.preStart)) {
    const text = `시작 전 원가까지 넣으면 마진 ${formatWon(round2(margin - billable.preStart))}`;
    referenceMarginText = referenceMarginText ? `${referenceMarginText} · ${text}` : `참고 · ${text}`;
  }

  if (notBillable) {
    return {
      flags: { legacy, priced, manualOnly, internal, drift, live: !!live, startMonth: false, notBillable: true },
      source: { label: '', tone: sourceTone },
      legacyNext,
      legacyText,
      planMissingText: '',
      planMissingLinkText: '',
      rows,
      totals: {
        cost: wonCell(summary.cost.total),
        supply: NONE,
        vat: NONE,
        totalWithVat: NONE,
        margin: NONE,
        notes: [NOT_BILLABLE_COST_NOTE],
        partialTip,
      },
      referenceMarginText: '',
    };
  }

  return {
    flags: { legacy, priced, manualOnly, internal, drift, live: !!live, startMonth, notBillable: false },
    source: { label: sourceLabel, tone: sourceTone },
    legacyNext,
    legacyText,
    planMissingText,
    planMissingLinkText,
    rows,
    totals: {
      cost: wonCell(summary.cost.total),
      supply: wonCell(supply),
      vat: wonCell(vat),
      totalWithVat: wonCell(totalWithVat),
      margin: wonCell(margin, { negative: isNegative(margin) }),
      notes,
      partialTip,
    },
    referenceMarginText,
  };
}

/**
 * 상담사 번호 줄의 `peak_numbers`(번호 이름·끝 4자리)를 뺀 상세 — withNames:false. 원본은 바꾸지 않는다.
 * @param {object} detail
 * @returns {object}
 */
function withoutPeakNames(detail) {
  const line = detail?.charge?.lines?.counselor_number;
  if (!obj(line) || !Array.isArray(line.peak_numbers)) return detail;
  return {
    ...detail,
    charge: {
      ...detail.charge,
      lines: { ...detail.charge.lines, counselor_number: { ...line, peak_numbers: [], peak_numbers_truncated: false } },
    },
  };
}
